<script lang="ts">
import Vue from 'vue'

export default Vue.extend({
  name: 'AsyncButton',
  props: {
    text: {
      type: String,
      required: true
    },
    waiting: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    success: {
      type: Boolean,
      default: false
    },
    confirm: {
      type: String,
      default: ''
    },
    displayErrorTimeout: {
      type: Number,
      default: 1000
    },
    displaySuccessTimeout: {
      type: Number,
      default: -1
    },
    type: {
      type: String,
      default: 'primary',
      validator: value => {
        return ['primary', 'transparent'].includes(value)
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    colour: {
      type: String,
      required: false
    }
  },
  data(): { displayError: boolean; displaySuccess: boolean } {
    return {
      displayError: false,
      displaySuccess: false
    }
  },
  computed: {
    buttonText(): string {
      if (this.waiting) return 'Submitting...'
      else if (this.displayError) return 'Error'
      else if (this.displaySuccess) return 'Success'
      else return this.text
    },
    colourModifierClass(): string | null {
      if (this.colour) {
        return `button--${this.colour}`
      } else {
        return null
      }
    },
    isDisabled(): boolean {
      return this.disabled || this.waiting
    }
  },
  watch: {
    error() {
      if (this.error) {
        this.showError()
      } else {
        this.displayError = false
      }
    },
    success() {
      if (this.success) {
        this.showSuccess()
      } else {
        this.displaySuccess = false
      }
    }
  },
  created() {
    if (this.error) {
      this.showError()
    }
    if (this.success) {
      this.showSuccess()
    }
  },
  methods: {
    handleClick() {
      if (this.confirm) {
        const confirmed = confirm(this.confirm)
        if (confirmed) {
          this.$emit('click')
        }
      } else {
        this.$emit('click')
      }
    },
    showError() {
      this.displayError = true
      setTimeout(() => {
        this.displayError = false
      }, this.displayErrorTimeout)
    },
    showSuccess() {
      this.displaySuccess = true
      if (this.displaySuccessTimeout >= 0) {
        setTimeout(() => {
          this.displaySuccess = false
        }, this.displaySuccessTimeout)
      }
    }
  }
})
</script>

<template lang="pug">
button.button.button--async.tw-py-3.tw-px-8(
  @click.prevent='handleClick'
  :class='[{ error: displayError }, { success: displaySuccess }, { waiting: waiting }, colourModifierClass, type]'
  tabindex=0
  @keyup.enter='handleClick'
  :disabled='isDisabled'
)
  span.spinner(v-if='waiting')
  span.label {{ buttonText }}
</template>

<style lang="stylus">
@import '../../assets/styles/modules/*'
.button.button--async
  transition: 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) all
  position: relative
  &.disabled, &:disabled
    cursor: not-allowed
  &.waiting
    pointer: default
    background-color: THRIVA.colours.navy
    color: THRIVA.colours.white
    &:hover, &:active, &:focus
      transform: translateY(0)
      box-shadow: none
    .label
      padding-left: 30px
  &.error
    background-color: THRIVA.colours.cherry
    border-color: THRIVA.colours.cherry
    cursor: default
    &:hover, &:active, &:focus
      transform: translateY(0)
      box-shadow: none
      background-color: THRIVA.colours.cherry
      border-color: THRIVA.colours.cherry
  &.success
    background-color: THRIVA.colours.basil
    border-color: THRIVA.colours.basil
    cursor: default
    &:hover, &:active, &:focus
      transform: translateY(0)
      box-shadow: none
      background-color: THRIVA.colours.basil
      border-color: THRIVA.colours.basil
  &.transparent
    .spinner
      &:before
        border-top-color: THRIVA.colours.carbon
        border-left-color: THRIVA.colours.carbon
  @keyframes spinner
    to {transform: rotate(360deg)}
.spinner
  margin-right: 8px
  &:before
    position: absolute
    content: ''
    top: 10px
    height: 16px
    width: 16px
    z-index: 1
    border: 3px solid transparent
    border-top-color: THRIVA.colours.white
    border-left-color: THRIVA.colours.white
    border-radius: 50%
    animation: spinner 0.5s linear infinite
</style>
